import { ITeamMembersConveyanceList } from "../../../../../Types/TeamMembers";
import { UncontrolledTooltip } from "reactstrap";
import styled, { createGlobalStyle } from "styled-components";
import {
  ActionGroup,
  ActionIconButton,
  CellRow,
  EditShell,
  EditInput,
  ErrorText,
  FullWidthCell,
  StatusIcon,
} from "../table/ConveyanceTable.style";
import IconComponent from "../../../../../CommonElements/IconComponent";
import { NA } from "../../../../../utils/Constant";

type ApprovalHistoryItem = ITeamMembersConveyanceList["last_approval"][number];

const ApprovalTooltipGlobalStyle = createGlobalStyle`
  .approval-history-tooltip {
    opacity: 1 !important;
    --approval-tooltip-bg: #ffffff;
    --approval-tooltip-border: #dbe3ef;
    --approval-tooltip-title: #0f172a;
    --approval-tooltip-meta: #64748b;
    --approval-tooltip-muted: #334155;
    --approval-tooltip-divider: #e2e8f0;
    --approval-tooltip-shadow: 0 14px 34px rgba(15, 23, 42, 0.16);
  }

  body.dark-only .approval-history-tooltip {
    --approval-tooltip-bg: #10101c;
    --approval-tooltip-border: rgba(106, 113, 133, 0.36);
    --approval-tooltip-title: #f8fafc;
    --approval-tooltip-meta: #cbd5e1;
    --approval-tooltip-muted: #e2e8f0;
    --approval-tooltip-divider: rgba(226, 232, 240, 0.18);
    --approval-tooltip-shadow: 0 14px 34px rgba(2, 6, 23, 0.36);
  }

  .approval-history-tooltip.show {
    opacity: 1 !important;
  }

  .approval-history-tooltip .approval-history-tooltip-inner {
    max-width: none !important;
    padding: 10px 11px !important;
    border: 1px solid var(--approval-tooltip-border) !important;
    border-radius: 8px !important;
    color: var(--approval-tooltip-title) !important;
    background: var(--approval-tooltip-bg) !important;
    box-shadow: var(--approval-tooltip-shadow) !important;
    text-align: left !important;
    opacity: 1 !important;
    backdrop-filter: none !important;
  }

  .approval-history-tooltip .tooltip-arrow::before {
    border-top-color: var(--approval-tooltip-bg) !important;
    border-bottom-color: var(--approval-tooltip-bg) !important;
  }
`;

const ApprovalTooltipContent = styled.div`
  display: grid;
  gap: 7px;
  width: min(280px, calc(100vw - 32px));
  text-align: left;
`;

const ApprovalTooltipItem = styled.div`
  padding-bottom: 7px;
  border-bottom: 1px solid var(--approval-tooltip-divider, #e2e8f0);

  &:last-child {
    padding-bottom: 0;
    border-bottom: 0;
  }
`;

const ApprovalTooltipTitle = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  color: var(--approval-tooltip-title, #0f172a);
  font-size: 11px;
  font-weight: 600;
  text-transform: capitalize;
`;

const ApprovalTooltipMeta = styled.p`
  margin: 3px 0 0;
  color: var(--approval-tooltip-meta, #64748b);
  font-size: 10px;
  line-height: 1.35;
`;

const ApprovalTooltipRemark = styled.p`
  margin: 4px 0 0;
  color: var(--approval-tooltip-muted, #334155);
  font-size: 10px;
  line-height: 1.35;
`;

const ApprovalTooltipEmpty = styled.p`
  margin: 0;
  color: var(--approval-tooltip-muted, #334155);
  font-size: 10px;
  line-height: 1.35;
`;

type Props = {
  row: ITeamMembersConveyanceList;
  keyId: string | number;
  draft: string;
  errorMsg: string | null;
  disabled: boolean;
  conveyanceStatus: string;
  onChange: (row: ITeamMembersConveyanceList, val: string) => void;
  onCommit: (row: ITeamMembersConveyanceList, val: string) => void;
  getApprovedValue: (row: ITeamMembersConveyanceList) => number;
  openConveyanceDialog: (
    type: "Approve" | "Reject",
    info: ITeamMembersConveyanceList,
  ) => void;
  isShowBtn: boolean;
  role: string;
  approvalHistory: ApprovalHistoryItem[];
  tooltipId: string;
};

const sanitizeTooltipId = (id: string | number) =>
  `approval-tooltip-${String(id).replace(/[^a-zA-Z0-9_-]/g, "-")}`;

const ApproveAmountCell = ({
  row,
  keyId,
  draft,
  errorMsg,
  disabled,
  onChange,
  onCommit,
  conveyanceStatus,
  getApprovedValue,
  openConveyanceDialog,
  isShowBtn,
  role,
  approvalHistory,
  tooltipId,
}: Props) => {
  const hasApprovalHistory = approvalHistory.length > 0;
  const safeTooltipId = sanitizeTooltipId(tooltipId || keyId);
  const canReviewAgain =
    ["manager", "hr"].includes(role) &&
    ["approved", "rejected"].includes(conveyanceStatus);
  const showApproveAction =
    ["pending", "rejected"].includes(conveyanceStatus) || canReviewAgain;
  const showRejectAction =
    ["pending", "approved"].includes(conveyanceStatus) || canReviewAgain;

  return (
    <FullWidthCell>
      <ApprovalTooltipGlobalStyle />
      <CellRow $hasError={!!errorMsg}>
        <EditShell $hasError={!!errorMsg}>
          <EditInput
            type="number"
            min={0}
            max={Number(row.amount ?? undefined)}
            value={draft}
            onChange={(e) => onChange(row, e.target.value)}
            onFocus={(e) => e.target.select()}
            onBlur={() => onCommit(row, draft)}
            onKeyDown={(e) => e.key === "Enter" && onCommit(row, draft)}
            disabled={disabled}
          />
        </EditShell>

        {["manager", "hr"].includes(role) &&
        ["approved", "rejected"].includes(conveyanceStatus) ? (
          <>
            <StatusIcon id={safeTooltipId} tabIndex={0}>
              <IconComponent
                className={`text-${
                  conveyanceStatus === "approved" ? "success" : "danger"
                } icofont icofont-${
                  conveyanceStatus === "approved" ? "check" : "close"
                }-circled f-18 pointer text-capitalize`}
              />
            </StatusIcon>

            <UncontrolledTooltip
              target={safeTooltipId}
              placement="top"
              autohide={false}
              container="body"
              trigger="hover focus"
              className="approval-history-tooltip"
              innerClassName="approval-history-tooltip-inner"
            >
              <ApprovalTooltipContent>
                {hasApprovalHistory ? (
                  approvalHistory.map((approval, index) => (
                    <ApprovalTooltipItem key={`${approval.date}-${index}`}>
                      <ApprovalTooltipTitle>
                        <span>
                          {approval.status || NA} by -{" "}
                          {approval?.approver_info?.name || NA} (
                          {approval.by || NA})
                        </span>
                      </ApprovalTooltipTitle>
                    </ApprovalTooltipItem>
                  ))
                ) : (
                  <ApprovalTooltipEmpty>
                    No approval history found
                  </ApprovalTooltipEmpty>
                )}
              </ApprovalTooltipContent>
            </UncontrolledTooltip>
          </>
        ) : null}

        {isShowBtn && (
          <ActionGroup>
            {showApproveAction && (
              <ActionIconButton
                type="button"
                $tone="approve"
                onClick={() =>
                  openConveyanceDialog("Approve", {
                    ...row,
                    approved_amount: getApprovedValue(row),
                  })
                }
                disabled={!!errorMsg || disabled}
                aria-label="Approve conveyance"
                title="Approve"
              >
                <IconComponent className="icofont icofont-check f-14" />
              </ActionIconButton>
            )}

            {showRejectAction && (
              <ActionIconButton
                type="button"
                $tone="reject"
                onClick={() =>
                  openConveyanceDialog("Reject", {
                    ...row,
                    approved_amount: getApprovedValue(row),
                  })
                }
                disabled={!!errorMsg || disabled}
                aria-label="Reject conveyance"
                title="Reject"
              >
                <IconComponent className="icofont icofont-close f-14" />
              </ActionIconButton>
            )}
          </ActionGroup>
        )}
      </CellRow>

      {errorMsg && <ErrorText>{errorMsg}</ErrorText>}
    </FullWidthCell>
  );
};

export default ApproveAmountCell;
